import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ChevronDown, ChevronUp, Sparkles, TrendingUp, Users, Target, Zap, Lock } from 'lucide-react';
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#a855f7'];

const DemoAnalysisShowcase = ({ darkMode, onLoginClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Demo analysis data - a compelling social media post example
  const demoAnalysis = {
    content: "Just launched my new product! 🚀 After months of hard work, I'm so excited to finally share this with you all. Click the link in bio to check it out!",
    content_type: "text",
    dominant_emotion: "joy",
    emotions: {
      joy: 70,
      trust: 65,
      anticipation: 60,
      excitement: 55,
      curiosity: 50
    },
    emotional_intensity: 0.82,
    overall_score: 78,
    audience_reactions: {
      primary_response: "Highly positive and engaged - strong excitement",
      engagement_prediction: "Very High",
      call_to_action_effectiveness: "Strong",
      message_clarity: "Clear and enthusiastic"
    },
    recommendations: [
      "Add urgency with limited-time benefit (e.g., '24-hour launch discount!')",
      "Include social proof (e.g., 'Join 1,000+ early adopters')",
      "Use more specific details about the product's main benefit",
      "Consider adding a hook in the first sentence to grab attention immediately"
    ],
    valence: 0.75,
    arousal_level: 0.82,
    dominance: 0.68
  };

  const emotionChartData = Object.entries(demoAnalysis.emotions)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([name, value]) => ({ 
      name: name.charAt(0).toUpperCase() + name.slice(1), 
      value: Math.round(value),
      fill: COLORS[Math.floor(Math.random() * COLORS.length)]
    }));

  const padData = [
    { metric: 'Pleasure', value: Math.round(demoAnalysis.valence * 100) },
    { metric: 'Arousal', value: Math.round(demoAnalysis.arousal_level * 100) },
    { metric: 'Dominance', value: Math.round(demoAnalysis.dominance * 100) }
  ];

  return (
    <div className={`${darkMode ? 'bg-gray-800/50' : 'bg-gradient-to-b from-indigo-50 to-purple-50'} py-8 md:py-12`}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sparkles className={`w-6 h-6 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
            <h2 className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>
              See emoQ.ai in Action
            </h2>
          </div>
          <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
            Check out this real analysis example. Login to analyze your own content!
          </p>
        </div>

        {/* Demo Analysis Card */}
        <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-200'} shadow-2xl`}>
          <CardContent className="p-6 md:p-8">
            {/* Original Content */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  📝 Sample Content (Social Media Post)
                </h3>
                <Badge variant="outline" className={`${darkMode ? 'border-blue-400 text-blue-400' : 'border-blue-600 text-blue-600'}`}>
                  Demo Analysis
                </Badge>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-sm md:text-base ${darkMode ? 'text-gray-200' : 'text-gray-800'} italic`}>
                  "{demoAnalysis.content}"
                </p>
              </div>
            </div>

            {/* Quick Results Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-blue-900/50 to-blue-800/50 border border-blue-700' : 'bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-300'}`}>
                <div className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                  {demoAnalysis.overall_score}
                </div>
                <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                  Overall Score
                </div>
              </div>
              
              <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-purple-900/50 to-purple-800/50 border border-purple-700' : 'bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-300'}`}>
                <div className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-purple-400' : 'text-purple-700'} capitalize`}>
                  {demoAnalysis.dominant_emotion}
                </div>
                <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                  Dominant Emotion
                </div>
              </div>
              
              <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-green-900/50 to-green-800/50 border border-green-700' : 'bg-gradient-to-br from-green-100 to-green-200 border border-green-300'}`}>
                <div className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                  82%
                </div>
                <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                  Intensity
                </div>
              </div>
              
              <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-br from-orange-900/50 to-orange-800/50 border border-orange-700' : 'bg-gradient-to-br from-orange-100 to-orange-200 border border-orange-300'}`}>
                <div className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-700'}`}>
                  Very High
                </div>
                <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                  Engagement
                </div>
              </div>
            </div>

            {/* Toggle Button */}
            <div className="text-center mb-6">
              <Button 
                onClick={() => setIsExpanded(!isExpanded)}
                variant="outline"
                className={`${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-blue-300 hover:bg-blue-50'}`}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    See Detailed Analysis
                  </>
                )}
              </Button>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="space-y-6 animate-in fade-in duration-300">
                {/* Emotion Breakdown */}
                <div>
                  <h4 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Target className="w-5 h-5" />
                    Top Emotions Detected
                  </h4>
                  <div className="grid gap-3">
                    {Object.entries(demoAnalysis.emotions)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([emotion, score]) => (
                        <div key={emotion}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className={`capitalize font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {emotion}
                            </span>
                            <span className={`font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              {Math.round(score)}%
                            </span>
                          </div>
                          <Progress value={score} className="h-2" />
                        </div>
                      ))}
                  </div>
                </div>

                {/* Charts */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Emotion Bar Chart */}
                  <div>
                    <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Emotion Intensity
                    </h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={emotionChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                            border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* PAD Model */}
                  <div>
                    <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Emotional Dimensions
                    </h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <RadarChart data={padData}>
                        <PolarGrid stroke={darkMode ? '#374151' : '#e5e7eb'} />
                        <PolarAngleAxis 
                          dataKey="metric" 
                          tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                        />
                        <PolarRadiusAxis 
                          angle={90} 
                          domain={[0, 100]}
                          tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                        />
                        <Radar 
                          name="Score" 
                          dataKey="value" 
                          stroke="#8b5cf6" 
                          fill="#8b5cf6" 
                          fillOpacity={0.6} 
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                            border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Audience Reactions */}
                <div>
                  <h4 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Users className="w-5 h-5" />
                    Audience Insights
                  </h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {Object.entries(demoAnalysis.audience_reactions).map(([key, value]) => (
                      <div 
                        key={key}
                        className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}
                      >
                        <div className={`text-xs font-semibold uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {key.replace(/_/g, ' ')}
                        </div>
                        <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                <div>
                  <h4 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <TrendingUp className="w-5 h-5" />
                    AI-Powered Recommendations
                  </h4>
                  <div className="space-y-3">
                    {demoAnalysis.recommendations.map((rec, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg flex items-start gap-3 ${darkMode ? 'bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-700' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200'}`}
                      >
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-600 text-white'}`}>
                          {idx + 1}
                        </div>
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          {rec}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Blurred Additional Features Teaser */}
                <div className="relative mt-6">
                  <div className="filter blur-sm pointer-events-none">
                    <h4 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Platform-Specific Recommendations
                    </h4>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="font-bold mb-2">Instagram</div>
                        <div className="text-sm">Score: 85/100</div>
                        <div className="text-xs mt-2">Best time: 6-9 PM weekdays</div>
                      </div>
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="font-bold mb-2">LinkedIn</div>
                        <div className="text-sm">Score: 90/100</div>
                        <div className="text-xs mt-2">Best time: 7-9 AM weekdays</div>
                      </div>
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="font-bold mb-2">Twitter</div>
                        <div className="text-sm">Score: 75/100</div>
                        <div className="text-xs mt-2">Best time: 12-3 PM weekdays</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lock Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`text-center p-6 rounded-xl ${darkMode ? 'bg-gray-800/95 border border-gray-700' : 'bg-white/95 border border-gray-300'} shadow-2xl max-w-sm`}>
                      <Lock className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
                      <h5 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Unlock Full Analysis
                      </h5>
                      <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Get platform recommendations, timing insights, and more!
                      </p>
                      <Button 
                        onClick={onLoginClick}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        Sign Up for Free
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CTA Section */}
            <div className={`mt-8 p-6 rounded-xl text-center ${darkMode ? 'bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-700' : 'bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-300'}`}>
              <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Ready to Analyze Your Content?
              </h4>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Get 25 free credits when you sign up. No credit card required!
              </p>
              <Button 
                onClick={onLoginClick}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Start Free Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DemoAnalysisShowcase;
